import React, {
  useState,
  useEffect,
  memo,
  Fragment,
  useMemo,
} from "react";
import { Row, Col, Dropdown, Button } from "react-bootstrap";
import Card from "../../components/Card";
import StatCard from "./components/SKPStatCard";
import "./StatistikHeader.css";

import {
  FaCertificate,
  FaIndustry,
  FaMapMarkedAlt,
  FaCity,
} from "react-icons/fa";

// Dashboard Service
import DashboardService from "../../services/pbumku/DashboardService";

// Shared Filter Utils (SAMAKAN DENGAN DASHPRIMER)
import PeriodFilter from "./components/filters/PeriodFilter";
import { formatPeriodLabel } from "./utils/formatPeriodLabel";
import { validatePeriod } from "./utils/validatePeriod";

// Components
import SKPChart from "./components/skp-chart";
import { HACCPChart5 } from "./components/haccp-chart-5";
import HACCPChart6 from "./components/haccp-chart-6";

/* =========================
 * DATE UTIL (STANDARD)
 * ========================= */
const currentYear = new Date().getFullYear();
const getStartOfYear = () => `${currentYear}-01-01`;
const getEndOfYear = () => `${currentYear}-12-31`;

const DashPasca = memo(() => {
  const DashService = useMemo(() => new DashboardService(), []);

  const [loading, setLoading] = useState(false);
  const [isTrader, setIsTrader] = useState(false);

  /* =========================
   * PERIOD STATE (STANDARD)
   * ========================= */
  const [tempStartDate, setTempStartDate] = useState(getStartOfYear());
  const [tempEndDate, setTempEndDate] = useState(getEndOfYear());

  const [appliedStartDate, setAppliedStartDate] =
    useState(getStartOfYear());
  const [appliedEndDate, setAppliedEndDate] =
    useState(getEndOfYear());

  /* =========================
   * DATA STATE
   * ========================= */
  const [haccp, setHaccp] = useState([]);
  const [jmlHaccp, setJmlHaccp] = useState({});

  const [rekapSKP, setRekapSKP] = useState([]);
  const [limitSKP, setLimitSKP] = useState(10);
  const [resumeSKP, setResumeSKP] = useState({});

  /* =========================
   * EFFECT: ROLE
   * ========================= */
  useEffect(() => {
    const role = localStorage.getItem("role");
    setIsTrader(new Set(["4", "5"]).has(role));
  }, []);

  /* =========================
   * EFFECT: FETCH DATA
   * ========================= */
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedStartDate, appliedEndDate, limitSKP]);

  /* =========================
   * HANDLER FILTER
   * ========================= */
  const handleClickButton = () => {
    const validation = validatePeriod(
      tempStartDate,
      tempEndDate,
      {
        maxYearRange: 1,
        allowCrossYear: false,
      }
    );

    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    setAppliedStartDate(tempStartDate);
    setAppliedEndDate(tempEndDate);
  };

  /* =========================
   * FETCH ALL DATA
   * ========================= */
  const fetchAllData = async () => {
    try {
      setLoading(true);

      const start = appliedStartDate;
      const end = appliedEndDate;

      const [haccpRes, jmlRes, skpRes, resumeRes] = await Promise.all([
        DashService.getHACCPBulan(start, end),
        DashService.getJumlahHACCP(start, end),
        DashService.getSKPPerPropinsi(start, end, limitSKP),
        DashService.getResumeSKP(start, end),
      ]);

      setHaccp(haccpRes?.data || []);
      setJmlHaccp(jmlRes?.data || {});
      setRekapSKP(skpRes?.data || []);
      setResumeSKP(resumeRes?.data || {});
    } catch (error) {
      console.error("DashPasca fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSKP = (eventKey) => {
    setLimitSKP(eventKey === "all" ? null : Number(eventKey));
  };

  /* =========================
   * RENDER
   * ========================= */
  return (
    <Fragment>
      {!isTrader ? (
        <Fragment>
          {/* ===== HEADER ===== */}
          <Card className="mb-10 bg-transparent border-0 shadow-none">
            <Card.Body className="px-0">
              <Row className="align-items-end">
                <Col md={6}>
                  <h3 className="text-white fw-bold mb-2">
                    Sertifikasi Mutu Pasca Panen
                  </h3>
                  <h5 className="text-white-50">
                    {formatPeriodLabel(
                      appliedStartDate,
                      appliedEndDate
                    )}
                  </h5>
                </Col>

                <Col
                  md={6}
                  className="d-flex justify-content-md-end mt-3 mt-md-0"
                >
                  <PeriodFilter
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    onChangeStart={setTempStartDate}
                    onChangeEnd={setTempEndDate}
                    onSubmit={handleClickButton}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ===== SKP CHART ===== */}
          <Row>
            <Col md="8">
              <Card className="mb-3">
                <Card.Header>
                  <Row className="align-items-center g-0">
                    <Col xs={8}>
                      <h5 className="mb-0 text-start">
                        Tabel Jumlah SKP Per Provinsi
                      </h5>
                    </Col>

                    <Col xs={4} className="text-end">
                      <Dropdown onSelect={handleSelectSKP}>
                        <Dropdown.Toggle
                          variant="outline-dark"
                          size="sm"
                        >
                          {limitSKP ? `Top : ${limitSKP}` : "Semua"}
                        </Dropdown.Toggle>

                        <Dropdown.Menu align="end">
                          <Dropdown.Item eventKey="all">
                            Semua
                          </Dropdown.Item>
                          {[5, 10, 15, 20].map((v) => (
                            <Dropdown.Item key={v} eventKey={v}>
                              {v}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </Col>
                  </Row>
                </Card.Header>

                <Card.Body>
                  {rekapSKP.length > 0 ? (
                    <SKPChart rekapSKP={rekapSKP} />
                  ) : (
                    <p>Memuat data chart...</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md="4">
              <Card className="mb-3">
                <Card.Header>
                  <h5 className="mb-5">
                    Rekapitulasi Statistik SKP
                  </h5>
                </Card.Header>

                <Card.Body>
                  <Row className="g-3">
                    <Col xs={6}>
                      <StatCard
                        title="Sertifikat"
                        value={resumeSKP.sertifikat}
                        icon={FaCertificate}
                        color="#008FFB"
                      />
                    </Col>

                    <Col xs={6}>
                      <StatCard
                        title="UPI"
                        value={resumeSKP.upi}
                        icon={FaIndustry}
                        color="#00E396"
                      />
                    </Col>

                    <Col xs={6}>
                      <StatCard
                        title="Propinsi"
                        value={resumeSKP.provinsi}
                        icon={FaMapMarkedAlt}
                        color="#FEB019"
                      />
                    </Col>

                    <Col xs={6}>
                      <StatCard
                        title="Kabupaten"
                        value={resumeSKP.kabupaten}
                        icon={FaCity}
                        color="#775DD0"
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            {/* ===== HACCP ===== */}
            <Col md="12">
              <Card className="bg-white">
                <Card.Body>
                  {/* <Row className="mb-3">
                    <Col lg="3" md="6">
                      <StatCard
                        bgColor="bg-soft-warning"
                        svgIcon={iconDoc}
                        value={haccp2}
                        label="Sertifikasi HACCP"
                      />
                    </Col>
                    <Col lg="3" md="6">
                      <StatCard
                        bgColor="bg-soft-info"
                        svgIcon={iconHome}
                        value={upi}
                        label="UPI Tersertifikasi"
                      />
                    </Col>
                    <Col lg="3" md="6">
                      <StatCard
                        bgColor="bg-soft-primary"
                        svgIcon={iconUserPlus}
                        value={upn}
                        label="UPI Baru"
                      />
                    </Col>
                    <Col lg="3" md="6">
                      <StatCard
                        bgColor="bg-soft-danger"
                        svgIcon={iconDoc2}
                        value={upi - upn}
                        label="UPI Lama"
                      />
                    </Col>
                  </Row> */}

                  <Row className="g-3">
                    <Col md="8">
                      <Card>
                        <Card.Header>
                          <h5 className="mb-0">
                            Jumlah Sertifikat HACCP Terbit
                          </h5>
                        </Card.Header>
                        <Card.Body>
                          <HACCPChart5 haccp={haccp} />
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md="4">
                      <Card className="h-100">
                        <Card.Body className="text-center d-flex flex-column align-items-center">
                          <h5 className="mb-5">Total HACCP Terbit</h5>
                          <div style={{ position: "relative", width: 250, height: 250, display: "flex", alignItems: "center", justifyContent: "center", }}>
                            {/* Text tengah */}
                            <div style={{ position: "absolute", textAlign: "center", pointerEvents: "none", }}>
                              {/* <p style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
                                {jmlHaccp.JUMLAH}
                              </p> */}
                            </div>
                            <HACCPChart6 jmlHaccp={jmlHaccp} />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Fragment>
      ) : (
        <Row>
          <Col md="12">
            <h1 className="text-white">SIAP MUTU</h1>
          </Col>
        </Row>
      )}
    </Fragment>
  );
});

export default DashPasca;
